<script setup lang="ts">
import { DeleteConfirmationModal } from '@/components/common/DeleteConfirmationModal'
import RcMinerEditableDetail from '@/components/rc/miner-repairs/repairs-sidebar/RcMinerEditableDetail.vue'
import { useCBModelOptions } from '@/composables/rc/control-board-model-options'
import { useRepairsContext } from '@/composables/rc/drawer-context'
import { useFanModelOptions } from '@/composables/rc/fan-model-options'
import { useHashboardModelOptions } from '@/composables/rc/hashboard-model-options'
import { useMinerDetails } from '@/composables/rc/miner-details'
import { useMinerModelOptions } from '@/composables/rc/miner-model-options'
import { useMinerRepairActions } from '@/composables/rc/miner-repair-actions'
import { usePartDetails } from '@/composables/rc/part-details'
import { usePSUModelOptions } from '@/composables/rc/power-supply-model-options'
import { useOIDCStore } from '@/stores/oidc.store'
import type { IModel } from '@/types/rc/miners'
import { IndividualPart, partIcons, PartType, partTypes } from '@/types/rc/miners/PartType'
import { generateModelValueKey } from '@/util/rc'
import { Icon } from '@iconify/vue'
import type { CheckboxValueType } from 'element-plus'
import { storeToRefs } from 'pinia'
import { computed, ref, watch } from 'vue'

const oidcStore = useOIDCStore()
const { isRcAppManagerUser, isRcAppOperator } = storeToRefs(oidcStore)

const { minerId, selectedPart } = useRepairsContext()
const { minerDetailsQuery } = useMinerDetails(minerId)
const { partSn, partPcbId, partModel, partModelVersion, isReplacement, partStatus, partType, movedToInventory } =
  usePartDetails(minerId, selectedPart)

const icon = computed(() => partIcons[partType.value])

const { data: minerDetails } = minerDetailsQuery

const { updateHashboardOnlyMutation } = useMinerRepairActions(minerId)

const {
  isFetching: minerModelOptionsLoading,
  isError: minerModelOptionsQueryError,
  minerModelNameOptions: minerOptions,
  refetch: refetchMinerModelOptions,
} = useMinerModelOptions({ enabled: ref(false) })

const {
  isFetching: psuModelOptionsLoading,
  isError: psuModelOptionsQueryError,
  data: psuOptions,
  refetch: refetchPSUModelOptions,
} = usePSUModelOptions({ enabled: ref(false) })

const {
  isFetching: cbModelOptionsLoading,
  isError: cbModelOptionsQueryError,
  data: cbOptions,
  refetch: refetchCBModelOptions,
} = useCBModelOptions({ enabled: ref(false) })

const {
  isFetching: hashboardModelOptionsLoading,
  isError: hashboardModelOptionsQueryError,
  data: hashboardOptions,
  refetch: refetchHashboardModelOptions,
} = useHashboardModelOptions({
  enabled: ref(false),
})

const {
  isFetching: fanModelOptionsLoading,
  isError: fanModelOptionsQueryError,
  data: fanOptions,
  refetch: refetchFanModelOptions,
} = useFanModelOptions({
  enabled: ref(false),
})

// ref
const modelOptions = ref()

// computed
const isMiner = computed(() => partType.value === PartType.Miner)
const isPSU = computed(() => partType.value === PartType.PowerSupply)
const isCB = computed(() => partType.value === PartType.ControlBoard)
const isHashboard = computed(() => partType.value === PartType.Hashboard)
const isFan = computed(() => partType.value === PartType.Fan)

// Managers can edit every field from parts and from the miner
// Operators can edi every part field but they can't edit the miner
const canUserEditSerialNumber = computed(() => isRcAppManagerUser.value || (isRcAppOperator.value && !isMiner.value))
const canUserEditModel = computed(() => isRcAppManagerUser.value || (isRcAppOperator.value && !isMiner.value))
const canUserEditAssestTag = computed(() => isRcAppManagerUser.value || (isRcAppOperator.value && !isMiner.value))

const model = computed(() => {
  if (isMiner.value) return minerDetails.value?.model
  if (!partModel.value) return undefined

  return {
    name: partModel.value,
    version: partModelVersion.value,
    valueKey: generateModelValueKey(partModel.value, partModelVersion.value),
  } as IModel
})

const sn = computed(() => (isMiner.value ? minerDetails.value?.sn : partSn.value))
const assetTag = computed(() => minerDetails.value?.asset_tag ?? undefined)
const pcbId = computed(() => partPcbId.value)
const status = computed(() => (isMiner.value ? minerDetails.value?.status : partStatus.value))
const hashboardOnly = computed(() => minerDetails.value?.hashboard_only)

const modelOptionsLoading = computed(
  () =>
    (minerModelOptionsLoading.value && isMiner.value) ||
    (psuModelOptionsLoading.value && isPSU.value) ||
    (cbModelOptionsLoading.value && isCB.value) ||
    (hashboardModelOptionsLoading.value && isHashboard.value) ||
    (fanModelOptionsLoading.value && isFan.value)
)

const modelOptionsIsError = computed(
  () =>
    !modelOptionsLoading.value &&
    ((minerModelOptionsQueryError.value && isMiner.value) ||
      (psuModelOptionsQueryError.value && isPSU.value) ||
      (cbModelOptionsQueryError.value && isCB.value) ||
      (hashboardModelOptionsQueryError.value && isHashboard.value) ||
      (fanModelOptionsQueryError.value && isFan.value))
)

const disableModelSelect = computed(() => modelOptionsLoading.value || modelOptionsIsError.value)

// Set model options on part change
watch(selectedPart, async () => {
  modelOptions.value = undefined
  await setModelOptions(selectedPart.value)
})

/**
 * Fetches and generates an array of "model" ElSelect options given a part type.
 *
 * @param {IndividualPart} partType the individual part used to determine which options to fetch.
 */
const setModelOptions = async (partType: IndividualPart) => {
  switch (partTypes[partType]) {
    case PartType.Miner:
      await refetchMinerModelOptions()
      modelOptions.value = minerOptions.value
      break
    case PartType.PowerSupply:
      await refetchPSUModelOptions()
      modelOptions.value = psuOptions.value
      break
    case PartType.ControlBoard:
      await refetchCBModelOptions()
      modelOptions.value = cbOptions.value
      break
    case PartType.Hashboard:
      await refetchHashboardModelOptions()
      modelOptions.value = hashboardOptions.value
      break
    case PartType.Fan:
      await refetchFanModelOptions()
      modelOptions.value = fanOptions.value
      break
    default:
      break
  }
}

// Set initial model options
setModelOptions(selectedPart.value)

const onHashboardOnlyChange = (value: CheckboxValueType) => {
  const title = hashboardOnly.value ? 'Unmark miner as "Hashboard Only"' : 'Mark miner as "Hashboard Only"'
  const message = hashboardOnly.value
    ? 'Are you sure you want to unmark this miner as "Hashboard Only"?'
    : 'Are you sure you want to mark this miner as "Hashboard Only"? Associated PSU, CB and fans will be removed.'
  DeleteConfirmationModal(
    title,
    message,
    { hashboard_only: value as boolean },
    updateHashboardOnlyMutation.mutateAsync,
    () => {},
    'Update'
  )
}
</script>

<template>
  <el-container class="grow-0">
    <el-header class="p-0">
      <el-row>
        <el-col :span="8">
          <h2 class="heading"><Icon :icon="icon" />{{ selectedPart }}</h2>
        </el-col>
        <el-col v-if="selectedPart == IndividualPart.Miner" :span="4">
          <el-checkbox v-model="hashboardOnly" label="Hashboard Only" @change="onHashboardOnlyChange" />
        </el-col>
        <el-col :span="12"
          ><el-tag v-if="status" class="badge">{{ status }}</el-tag></el-col
        >
      </el-row>
    </el-header>
    <el-main :key="selectedPart" class="p-0">
      <el-row :gutter="8">
        <el-col :span="12" class="subheader">
          <p class="flex flex-row gap-1">
            Model
            <span v-if="modelOptionsIsError" class="text-error">
              <el-tooltip
                placement="top"
                content="Failed to fetch model options, edit
                locked."
              >
                <Icon icon="material-symbols:error-outline-rounded" height="12"
              /></el-tooltip>
            </span>
          </p>
          <RcMinerEditableDetail
            :required="isMiner"
            prop="model"
            :miner-id="minerId"
            :part-type="selectedPart"
            :val="model"
            :options="modelOptions"
            :options-value-key="isMiner ? undefined : 'valueKey'"
            :disabled="disableModelSelect || isReplacement || movedToInventory || !canUserEditModel"
            :clearable="!isMiner"
          />
        </el-col>
        <el-col :span="12" class="subheader">
          <p>SN</p>
          <RcMinerEditableDetail
            :required="isMiner"
            prop="sn"
            :miner-id="minerId"
            :part-type="selectedPart"
            :val="sn"
            clearable
            :disabled="isReplacement || !canUserEditSerialNumber || movedToInventory"
          />
        </el-col>
      </el-row>
      <el-row style="margin-top: 20px" :gutter="8">
        <el-col v-if="selectedPart == IndividualPart.Miner" :span="12" class="subheader">
          <p>Asset Tag</p>
          <RcMinerEditableDetail
            prop="asset_tag"
            :miner-id="minerId"
            :part-type="selectedPart"
            :val="assetTag"
            clearable
            :disabled="isReplacement || !canUserEditAssestTag"
          />
        </el-col>
        <el-col v-if="selectedPart != IndividualPart.Miner" :span="12" class="subheader">
          <p>PCB ID</p>
          <RcMinerEditableDetail
            prop="pcb_id"
            :miner-id="minerId"
            :part-type="selectedPart"
            :val="pcbId"
            clearable
            :disabled="isReplacement || movedToInventory"
          />
        </el-col>
      </el-row>
    </el-main>
  </el-container>
</template>

<style scoped lang="scss">
:deep() {
  .el-main {
    overflow: hidden;
  }
}

.heading {
  display: flex;
  align-items: center;
  gap: var(--spacing-spacing-xs);
}

.el-tag {
  padding: var(--spacing-spacing-xs);
  border-radius: var(--spacing-spacing-2xl);
  color: var(--text-badge-gray);
  border-color: var(--component-stroke-gray);
  background-color: var(--component-fill-gray);
  float: right;
}

.subheader {
  display: flex;
  flex-direction: column;
  gap: 10px;

  p:nth-of-type(1) {
    color: var(--text-tertiary);
  }
}
</style>
